// @react-compiler enable
import SessionElapsedTime from '@github-ui/agent-sessions/components/SessionElapsedTime'
import {getSessionStatusIcon} from '@github-ui/agent-sessions/components/StatusIcon'
import {useAgentInfo} from '@github-ui/agent-sessions/hooks/use-agent-info'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {testIdProps} from '@github-ui/test-id-props'
import {CopilotIcon} from '@primer/octicons-react'
import {memo} from 'react'

import {useTableCopilotTasks} from '../../features/copilot-tasks/use-table-copilot-tasks'
import type {MemexItemModel} from '../../models/memex-item-model'
import {shouldShowAgentTaskPill} from '../fields/agent-task-pill'
import styles from './agent-task-sub-row.module.css'

interface AgentTaskSubRowProps {
  model: MemexItemModel
  /** Hierarchy depth of the parent row (0 for top-level items). */
  depth?: number
}

function AgentTaskAgentIcon({agentId}: {agentId?: number}) {
  const {data: agentInfo} = useAgentInfo(agentId ?? null)

  const avatarUrl = agentInfo?.avatar_url
  const displayName = agentInfo?.display_name || 'Copilot'
  const isCopilot = !agentId || displayName.toLowerCase() === 'copilot'

  if (isCopilot || !avatarUrl) {
    return <CopilotIcon size={16} />
  }

  return <GitHubAvatar src={avatarUrl} alt={displayName} size={16} />
}

export const AgentTaskSubRow = memo(function AgentTaskSubRow({model, depth = 0}: AgentTaskSubRowProps) {
  const {copilotTasksEnabled} = useTableCopilotTasks()

  if (!copilotTasksEnabled) return null
  if (!shouldShowAgentTaskPill(model.agentTaskState, model.agentTaskCompletedAt)) {
    return null
  }

  // agentTaskState is guaranteed non-null after shouldShowAgentTaskPill check
  const agentTaskState = model.agentTaskState
  if (!agentTaskState) return null

  const statusIcon = getSessionStatusIcon(agentTaskState)
  const title = model.agentTaskTitle

  return (
    <div
      className={styles.AgentTaskSubRow}
      style={{'--agent-task-depth': depth} as React.CSSProperties}
      {...testIdProps('agent-task-sub-row')}
    >
      <span className={styles.statusIcon}>{statusIcon}</span>
      <span className={styles.agentIcon}>
        <AgentTaskAgentIcon agentId={model.agentTaskAgentId} />
      </span>
      {title && <span className={styles.title}>{title}</span>}
      {model.agentTaskCreatedAt && (
        <span className={styles.elapsedTime}>
          <SessionElapsedTime
            createdAt={model.agentTaskCreatedAt}
            completedAt={model.agentTaskCompletedAt}
            state={agentTaskState}
          />
        </span>
      )}
    </div>
  )
})
